<template>
  <div class="switch-organisme">
    <div
      v-if="selectIsLoading"
      class="loader flex-vcenter"
    >
      <InfiniteLoader class="loader" />
    </div>
    <SelectClassic
      v-else-if="allOrganismes.length > 1"
      v-model="selectedOrg"
      :label="label"
      :inline="inline"
      :status="required ? 'required' : null"
      :internal-search="false"
      :placeholder="$t('general.placeholder-selectionner')"
      :options="organismesList"
      :options-are-loading="optionsAreLoading"
      select-on-enter
      @search="fetchOrganismes"
      @close="switchOrganisme"
    >
      <template #label-icon>
        <UilBuilding size="16" />
      </template>
      <template #after-option-item="{ option, }">
        <!--
          @slot Emplacement pour un élément à droite de l'option.
          @binding {Object} option Informations de l'élément du dropdown.
        -->
        <slot
          name="after-option-item"
          :option="option"
        />
        <UilHistory
          v-if="option.recent"
          v-tooltip="{ content: $t('organismes.recemment-utilise'), }"
          class="organisme-recent"
        />
      </template>
    </SelectClassic>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import { SelectClassic, InfiniteLoader } from "@lde/core_lde_vue";
import { UilBuilding, UilHistory } from "@lde/vue-unicons";

import Api from "@/modules/axios";

/**
 * Affiche un select permettant de changer l'organisme courant. Applique la modification à la sélection contrairement
 * au composant SelectOrganismeInput.
 */
export default {
  name: "SwitchOrganisme",
  components: {
    SelectClassic,
    InfiniteLoader,
    UilBuilding,
    UilHistory,
  },
  props: {
    /**
     * Texte affiché dans le select (en haut).
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * Affiche l'astérisque signant l'obligation de remplir ce champ.
     */
    required: {
      type: Boolean,
      default: false,
    },
    /**
     * Affiche le composant en une ligne.
     */
    inline: {
      type: Boolean,
      default: false,
    },
    /**
     * Cache le tag dans la zone de l'input.
     */
    hideInputTag: {
      type: Boolean,
      defaut: false,
    },
  },
  data() {
    return {
      selectIsLoading: false,
      optionsAreLoading: false,
      allOrganismes: [],
      organismes: [],
      searchText: "",
      select: {},
    };
  },
  computed: {
    ...mapGetters(["organismeActuel"]),
    /**
     * Formattage de l'objet du store pour correspondre au format attendu par SelectClassic
     */
    selectedOrg: {
      get() {
        if (!this.organismeActuel) {
          return null;
        }
        return {
          label: this.organismeActuel.nom_complet,
          value: this.organismeActuel.nom_complet,
          id: this.organismeActuel.id,
          type: this.organismeActuel.type,
        };
      },
      set(org) {
        this.select = org;
      },
    },
    organismesList() {
      const recentsRaw = this.getRecentsOrganismes();
      // Si un texte de recherche est présent, on affiche les résultats de la recherche (this.organismes),
      // sinon on affiche la liste complète conservée (this.allOrganismes).
      const available = (this.searchText === "")
        ? this.allOrganismes
        : this.organismes;
      // Ne garder comme récents que ceux toujours accessibles à l'utilisateur
      const recentsFiltered = recentsRaw.filter((r) => available.some((a) => a.id === r.id));
      // Marquer les récents pour pouvoir afficher l'icône dans les items
      const recents = recentsFiltered.map((org) => ({ ...org, recent: true }));
      const autres = available
        .filter((org) => !recents.some((recent) => recent.id === org.id))
        .map((org) => ({ ...org, recent: false }));
      // Retourner une liste plate avec les récents en premier
      return [...recents, ...autres];
    },
  },
  mounted() {
    this.selectIsLoading = true;
    this.fetchOrganismes().then(() => {
      this.selectIsLoading = false;
      // Après le chargement initial des organismes, on valide les organismes "récents"
      // stockés en local auprès du back afin d'éviter de dépendre de la pagination.
      this.validateRecentsWithBackend();
      // Au cas où, on refetch les organismes récents
      this.fetchOrganismesRecents();
    });
  },
  methods: {
    /**
     * Récupère les organismes accessibles.
     * @param {String} searchText Texte tapé dans l'input.
     * @returns {Promise} Organismes accessibles.
     */
    fetchOrganismes(searchText = "") {
      // Garder le texte de recherche actuel afin que la computed puisse choisir la bonne source
      this.searchText = searchText;
      this.optionsAreLoading = true;
      return this.$store.dispatch("fetchOrganismes", searchText)
        .then((res) => {
          this.organismes = res.data.results.map(
            (org) => ({
              label: org.nom_complet,
              value: org.nom_complet,
              id: org.id,
              type: org.type,
            }),
          );
          // Permet de conserver la liste de tous les organismes pour savoir si l'utiliteur peut
          // changer d'organisme depuis le select.
          if (searchText === "") {
            this.allOrganismes = this.organismes;
          }
          this.optionsAreLoading = false;
        });
    },
    /**
     * Change d'organisme.
     */
    switchOrganisme() {
      if (this.select.id && this.select.id !== this.organismeActuel?.id) {
        // voir frontend/src/components/modals/ModalVerifyAccount.vue
        localStorage.removeItem("sessionFondsPropresAccept");

        // enregistre l'organisme
        this.saveRecentOrganisme(this.select);

        Api().post("/switch/", { organisme: this.select.id, chosen_org: true })
          .then(({ data }) => {
            this.$store.commit("setOrganismeActuel", data);
            // Redirection vers l'accueil sans router.push pour faire un reload pour redéclencher le identify et
            // éviter d'éventuels soucis
            window.location = "/";
          });
      }
    },
    /**
     * Enregistre les 5 derniers organismes choisis
     * @param {Object} newItem - Le nouvel organisme à sauvegarder
     */
    saveRecentOrganisme(newItem) {
      if (!newItem) {
        return;
      }
      let organismes = this.getRecentsOrganismes();
      // Filtrer les doublons en comparant les IDs
      organismes = organismes.filter((item) => item.id !== newItem.id);
      // Ajouter le nouvel organisme au début
      organismes.unshift(newItem);
      // Garder uniquement les 5 plus récents
      organismes = organismes.slice(0, 5);
      const userId = this.$store.getters.user?.id;
      if (!userId) {
        return;
      }
      localStorage.setItem(`recentsOrganismes:user:${userId}`, JSON.stringify(organismes, null, 2));
    },
    /**
     * Récupère les organismes récents du localStorage
     * @returns {Array} Liste des organismes récents
     */
    getRecentsOrganismes() {
      const userId = this.$store.getters.user?.id;
      if (!userId) {
        return [];
      }
      const data = localStorage.getItem(`recentsOrganismes:user:${userId}`);
      try {
        return data ? JSON.parse(data) : [];
      } catch (e) {
        console.error(e);
        localStorage.removeItem(`recentsOrganismes:user:${userId}`);
        return [];
      }
    },
    /**
     * Valide les organismes récents stockés en local auprès du backend
     * et remplace le localStorage si besoin pour ne garder que les IDs valides.
     */
    validateRecentsWithBackend() {
      const recents = this.getRecentsOrganismes();

      // Envoi au backend la liste d'IDs pour vérification
      const ids = recents.map((r) => r.id);
      return Api()
        .post("/organisme/validate_ids/", { ids })
        .then((res) => {
          const validIds = res.data.results || [];
          const filtered = recents.filter((r) => validIds.includes(r.id));
          // Si la liste a changé, on met à jour le localStorage
          if (filtered.length !== recents.length) {
            const userId = this.$store.getters.user?.id;
            if (userId) {
              localStorage.setItem(
                `recentsOrganismes:user:${userId}`,
                JSON.stringify(filtered, null, 2),
              );
            }
          }
        })
        .catch(() => undefined);
    },
    /**
     * Récupère les organismes récents
     * @params {String} organismeId
     * @returns {Promise Organismes récents}
     */
    fetchOrganismesRecents() {
      const recents = this.getRecentsOrganismes();
      const ids = recents.map((r) => r.id);

      if (!ids.length) {
        return Promise.resolve([]);
      }

      return Api()
        .get("/organisme/", { params: { id__in: ids.join(",") } })
        .then((res) => {
          const data = res.data && res.data.results ? res.data.results : res.data || [];
          // Transforme les objets API au format attendu par le Select
          const organismes = data.map((org) => ({
            label: org.nom_complet,
            value: org.nom_complet,
            id: org.id,
            type: org.type,
          }));

          // Insère en tête
          organismes.forEach((o) => {
            this.allOrganismes.unshift(o);
          });

          return organismes;
        })
        .catch(() => undefined);
    },
  },
};
</script>

<style lang="scss">
@use "~@lde/core_lde_vue/dist/assets/styles/_variables.scss" as var;

.switch-organisme {
  .loader { height: 100%; }

  .tag {
    margin-top: 0;
    margin-bottom: 0;
    white-space: nowrap;
  }

  .organisme-recent {
    margin-left: var.$space-x-tiny;
  }

}
</style>
